import React, { useState, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Combobox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import {
  PHONE_COUNTRIES,
  findCountryByCode,
  findCountryByDialCode,
  formatPhoneNumber,
  toE164Format
} from '../../services/PhoneCountryService';

const FormField = ({
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  type = 'text',
  disabled = false,
  options = null,
  defaultCountryCode = 'US',
  error = null,
  onNotFound = null,
  notFoundLabel = 'option'
}) => {
  const selectOptions = options || [];
  const [query, setQuery] = useState('');

  // Phone field state
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState(
    findCountryByCode(defaultCountryCode)
  );
  const [isPhoneDropdownOpen, setIsPhoneDropdownOpen] = useState(false);
  const [phoneSearchTerm, setPhoneSearchTerm] = useState('');
  const [localPhoneNumber, setLocalPhoneNumber] = useState('');

  // Filter phone countries based on search
  const filteredPhoneCountries = useMemo(() => {
    if (!phoneSearchTerm) return PHONE_COUNTRIES;
    return PHONE_COUNTRIES.filter(country =>
      country.name.toLowerCase().includes(phoneSearchTerm.toLowerCase()) ||
      country.dialCode.includes(phoneSearchTerm)
    );
  }, [phoneSearchTerm]);

  // Parse existing E.164 number on mount
  useEffect(() => {
    if (type === 'phone' && value && !localPhoneNumber) {
      const phoneValue = value.startsWith('+') ? value : `+1${value}`;
      const country = findCountryByDialCode(phoneValue);
      if (country) {
        setSelectedPhoneCountry(country);
        const localNumber = phoneValue.substring(country.dialCode.length);
        setLocalPhoneNumber(formatPhoneNumber(localNumber, country.format));
      }
    }
  }, [value, type]);

  // Update country when defaultCountryCode changes
  useEffect(() => {
    if (type === 'phone') {
      const country = defaultCountryCode ? findCountryByCode(defaultCountryCode) : null;
      const currentCode = selectedPhoneCountry?.code;

      if (country?.code !== currentCode) {
        setSelectedPhoneCountry(country);
        if (country && localPhoneNumber) {
          const cleaned = localPhoneNumber.replace(/\D/g, '');
          const reformatted = formatPhoneNumber(cleaned, country.format);
          setLocalPhoneNumber(reformatted);
          const e164 = toE164Format(country.dialCode, cleaned);
          onChange(e164);
        } else if (!country && localPhoneNumber) {
          const cleaned = localPhoneNumber.replace(/\D/g, '');
          setLocalPhoneNumber(cleaned);
          onChange(cleaned);
        }
      }
    }
  }, [defaultCountryCode, type]);

  const handlePhoneCountrySelect = (country) => {
    setSelectedPhoneCountry(country);
    setIsPhoneDropdownOpen(false);
    setPhoneSearchTerm('');

    const cleanedNumber = localPhoneNumber.replace(/\D/g, '');
    const reformatted = formatPhoneNumber(cleanedNumber, country.format);
    setLocalPhoneNumber(reformatted);

    const e164 = toE164Format(country.dialCode, cleanedNumber);
    onChange(e164);
  };

  const handlePhoneNumberChange = (e) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input, selectedPhoneCountry.format);
    setLocalPhoneNumber(formatted);

    const e164 = toE164Format(selectedPhoneCountry.dialCode, input);
    onChange(e164);
  };

  const filteredOptions =
    query === ''
      ? selectOptions
      : selectOptions.filter((option) =>
          option.label
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
        );

  const selectedOption = selectOptions.find((opt) => opt.value === value);

  return (
    <div className="flex flex-col gap-1">
      <label
        className="text-[12px] font-normal leading-[16px] text-[#252525] mb-2"
        style={{ fontFamily: 'Poppins, sans-serif' }}
      >
        {label}
      </label>

      {type === 'autocomplete' ? (
        <Combobox value={value} onChange={onChange} disabled={disabled}>
          <div className="relative">
            <div className={`relative w-full cursor-default overflow-hidden bg-white border
              border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[#49C44F]
              focus-within:border-transparent`}>
              <Combobox.Input
                className="w-full h-[56px] pl-4 pr-10 text-[14px] font-normal leading-[18px] text-[#252525]
                  border-none focus:ring-0 focus:outline-none
                  placeholder:text-[#6E6E6E]
                  disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Poppins, sans-serif' }}
                displayValue={() => selectedOption?.label || ''}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={placeholder}
              />
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </Combobox.Button>
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery('')}
            >
              <Combobox.Options className={`absolute z-10 mt-1 max-h-60 w-full overflow-auto
                rounded-lg bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5
                focus:outline-none list-none`}>
                {filteredOptions.length === 0 && query !== '' ? (
                  <div
                    className="relative cursor-default select-none py-2 px-4"
                    style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {onNotFound ? (
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); onNotFound(query); }}
                        className="text-[14px] text-[#49C44F] font-semibold hover:underline focus:outline-none"
                      >
                        {`Don't see your ${notFoundLabel}?`}
                      </button>
                    ) : (
                      <span className="text-gray-700">No results found.</span>
                    )}
                  </div>
                ) : (
                  filteredOptions.map((option) => (
                    <Combobox.Option
                      key={option.value}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-3 pl-10 pr-4 ${
                          active ? 'bg-[#49C44F] text-white' : 'text-gray-900'
                        }`
                      }
                      value={option.value}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={`block truncate text-[14px] ${
                              selected ? 'font-medium' : 'font-normal'
                            }`}
                            style={{ fontFamily: 'Poppins, sans-serif' }}
                          >
                            {option.label}
                          </span>
                          {selected ? (
                            <span
                              className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                active ? 'text-white' : 'text-[#49C44F]'
                              }`}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>
      ) : type === 'select' ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="
            h-[56px] px-4 text-[14px] font-normal leading-[18px] text-[#6E6E6E]
            bg-white border border-gray-300 rounded-lg
            focus:outline-none focus:ring-2 focus:ring-[#49C44F] focus:border-transparent
            disabled:opacity-50 disabled:cursor-not-allowed
          "
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          <option value="">{placeholder}</option>
          {selectOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : type === 'phone' ? (
        <div className="relative">
          <div className={`flex h-[56px] bg-white border border-gray-300 rounded-lg
            focus-within:ring-2 focus-within:ring-[#49C44F] focus-within:border-transparent
            overflow-hidden`}>
            {selectedPhoneCountry && (
              <button
                type="button"
                onClick={() => !disabled && setIsPhoneDropdownOpen(!isPhoneDropdownOpen)}
                disabled={disabled}
                className="flex items-center gap-2 px-3 border-r border-gray-300
                  hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <span className="text-xl">{selectedPhoneCountry.flag}</span>
                <span className="text-[14px] font-normal text-[#252525]">
                  {selectedPhoneCountry.dialCode}
                </span>
                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
              </button>
            )}

            <input
              type="tel"
              value={selectedPhoneCountry ? localPhoneNumber : value}
              onChange={selectedPhoneCountry ? handlePhoneNumberChange : (e) => onChange(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              className="flex-1 px-4 text-[14px] font-normal leading-[18px] text-[#252525]
                bg-transparent border-none outline-none
                placeholder:text-[#6E6E6E]
                disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>

          {selectedPhoneCountry && isPhoneDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsPhoneDropdownOpen(false)}
              />
              <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white
                border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={phoneSearchTerm}
                  onChange={(e) => setPhoneSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 text-[14px] border-b border-gray-200
                    outline-none placeholder:text-[#6E6E6E]"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                />

                <div className="max-h-60 overflow-y-auto">
                  {filteredPhoneCountries.map((country) => (
                    <div
                      key={country.code}
                      onClick={() => handlePhoneCountrySelect(country)}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer
                        hover:bg-gray-50 transition-colors
                        ${selectedPhoneCountry.code === country.code ? 'bg-green-50' : ''}`}
                    >
                      <span className="text-xl">{country.flag}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[14px] text-[#252525] truncate"
                          style={{ fontFamily: 'Poppins, sans-serif' }}>
                          {country.name}
                        </div>
                      </div>
                      <span className="text-[14px] text-[#6E6E6E]"
                        style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {country.dialCode}
                      </span>
                      {selectedPhoneCountry.code === country.code && (
                        <CheckIcon className="h-5 w-5 text-[#49C44F]" />
                      )}
                    </div>
                  ))}
                  {filteredPhoneCountries.length === 0 && (
                    <div className="px-4 py-3 text-[14px] text-[#6E6E6E]"
                      style={{ fontFamily: 'Poppins, sans-serif' }}>
                      No countries found
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          className="
            h-[56px] px-4 text-[14px] font-normal leading-[18px] text-[#6E6E6E]
            bg-white border border-gray-300 rounded-lg
            focus:outline-none focus:ring-2 focus:ring-[#49C44F] focus:border-transparent
            placeholder:text-[#6E6E6E]
            disabled:opacity-50 disabled:cursor-not-allowed
          "
          style={{ fontFamily: 'Poppins, sans-serif' }}
        />
      )}
      {error && (
        <span
          className="text-[12px] text-red-600 leading-[16px]"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

FormField.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  defaultCountryCode: PropTypes.string,
  error: PropTypes.string,
  onNotFound: PropTypes.func,
  notFoundLabel: PropTypes.string
};

export default FormField;
